import React, { useContext, memo } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import CoreProperties from '@prisjakt/ui-core-properties';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';
import T from '@prisjakt/ui-translate';
import { AppContext } from '@prisjakt/ui-app-context';
import Viewport from '../../../../components/viewport';
import ErrorBoundary from '../../../../components/error-boundary';

import StoreCount from './components/store-count';
import PriceSummary from './components/price-summary';
import CategoryRank from './components/category-rank';
import CompareLink from './components/compare-link';
import UserSentiments from './components/user-sentiments';

import * as S from './styles';

const onError = () => null;

const ReadableProductInformation = ({ product }) => {
  const { t, linkTo, market } = useContext(AppContext);

  if (
    !product ||
    !market.modules.readableProductInfo ||
    !market.modules.readableProductInfo.enabled
  ) {
    return null;
  }

  const {
    id,
    pathName,
    name,
    priceSummary,
    prices,
    category,
    popularity,
    description,
    coreProperties,
    related,
    reviewBadges,
    userReviewSummary,
  } = product;

  return (
    <ErrorBoundary onError={onError}>
      <Panel
        header={
          <PanelHeader
            title={
              <Link to={linkTo('productProperties', { id, pathName })}>
                {t('product', 'product-info.title')}
              </Link>
            }
          >
            <S.ColoredLink to={linkTo('productProperties', { id, pathName })}>
              <T domain="product">product-info.show-more</T>
            </S.ColoredLink>
          </PanelHeader>
        }
        data-test-type="product-info"
      >
        {description && (
          <Viewport mobile>
            <S.ProductInfo
              dangerouslySetInnerHTML={{
                __html: description,
              }}
            />
          </Viewport>
        )}
        <S.ProductInfo>
          <PriceSummary
            productName={name}
            priceSummary={priceSummary}
            prices={prices}
          />{' '}
          <CategoryRank
            name={name}
            category={category}
            popularity={popularity}
            userReviewSummary={userReviewSummary}
          />{' '}
          <UserSentiments
            id={id}
            pathName={pathName}
            reviewBadges={reviewBadges}
          />{' '}
          <CompareLink
            product={product}
            category={category}
            relatedProduct={
              related &&
              related.products &&
              related.products.length &&
              related.products[0].product
            }
          />{' '}
          <StoreCount name={name} prices={prices} />
        </S.ProductInfo>
        {coreProperties && (
          <CoreProperties
            toneDown
            size="small"
            view="table"
            properties={coreProperties.nodes}
          />
        )}
      </Panel>
    </ErrorBoundary>
  );
};

ReadableProductInformation.propTypes = {
  product: PropTypes.object,
};

ReadableProductInformation.defaultProps = {
  product: undefined,
};

export default memo(ReadableProductInformation);
